<i18n>
{
  "de": {
    "nothingToSee": "Hier gibt es bisher noch nichts zu sehen...",
    "leaveComment": "Hinterlasse einen Kommentar",
    "whatWouldShare": "Was möchtest du teilen?",
    "tell": "Teile",
    "whatThink": "mit, was Du denkst...",
    "pinnedCommentsHeading": "Hervorgehobene Beiträge",
    "loadMore": "Mehr laden",
    "accessDisabledTitle": "Community nicht aktiviert",
    "accessDisabledDescription": "Die Community ist momentan nicht aktiviert.",
    "accessAuthenticatedTitle": "Anmeldung notwendig",
    "accessAuthenticatedDescription": "Um die Community zu nutzen, musst Du bei getnext angemeldet sein.",
    "accessFollowerTitle": "Nur für Follower",
    "accessFollowerDescription": "Folge {nickname} und nimm an der Community teil.",
    "accessRewardTitle": "Mitgliedschaft notwendig",
    "accessRewardDescription": "Um an der Community teilnehmen zu können, musst Du <a href=\"{rewardsOverviewUrl}\">Mitglied sein</a>.",
    "loginAndJoinCommunity": "Melde Dich an und nimm an<br />{nickname}s Community teil.",
    "pageTitle": "Gästebuch"
  },
  "en": {
    "nothingToSee": "Nothing to see here yet...",
    "leaveComment": "Leave a comment",
    "whatWouldShare": "What would you like to share?",
    "tell": "Tell",
    "whatThink": "what you think...",
    "pinnedCommentsHeading": "Highlighted postings",
    "loadMore": "Load more",
    "accessDisabledTitle": "Guestbook is not activated",
    "accessDisabledDescription": "The guestbook has not been activated yet.",
    "accessAuthenticatedTitle": "Sign-in required",
    "accessAuthenticatedDescription": "To access the guestbook, you must be logged in to getnext.",
    "accessFollowerTitle": "Only for followers",
    "accessFollowerDescription": "Follow {nickname} and participate in the guestbook.",
    "accessRewardTitle": "Subscription necessary",
    "accessRewardDescription": "To participate in the guestbook, you need to <a href=\"{rewardsOverviewUrl}\">subscribe</a>.",
    "loginAndJoinCommunity": "Log in and join<br/>{nickname}'s community",
    "pageTitle": "Guestbook"
  }
}
</i18n>

<template>
  <section class="section mt-20">
    <div class="container">
      <div class="columns">
        <div
          class="column"
          :class="isMobileView ? 'is-hidden' : 'is-one-quarter'"
        >
          <sidenav />
        </div>
        <div
          v-if="guestbookAccess.granted"
          class="community_content column is-half on-body-background"
        >
          <editor
            :editorId="rootEditorId"
            :options="editorOptions"
            :targetPost="null"
            @editormessage="handleEditorMessage"
          />

          <hr v-if="regularCommunityComments.length && !pinnedCommunityComments.length">

          <div
            v-if="!regularCommunityComments.length && !pinnedCommunityComments.length"
            class="box mt-25"
          >
            {{ $t('nothingToSee') }}
            <span>{{ $t('leaveComment') }}</span>
          </div>

          <div
            v-if="pinnedCommunityComments.length"
            class="pinned-comments mt-25 mb-25"
          >
            <h3>{{ $t('pinnedCommentsHeading') }}</h3>
            <profile-post-community
              v-for="(comment, index) in pinnedCommunityComments"
              :key="comment._id"
              :user="artist"
              :comment="comment"
              :index="index"
              :community="true"
              :pinned="true"
              :level="0"
              @deletecomment="deleteComment"
              @pinned="handlePinned"
            />
            <hr>
          </div>

          <profile-post-community
            v-for="(comment, index) in regularCommunityComments"
            :key="comment._id"
            :user="artist"
            :comment="comment"
            :index="index"
            :community="true"
            :level="0"
            @pinned="handlePinned"
            @deletecomment="deleteComment"
          />
          <div
            v-if="regularCommunityComments.length < artist.communityCommentsFirstLevelCount - pinnedCommunityComments.length"
            class="button is-primary is-fullwidth"
            @click="loadMoreCommunityComments(artist.communityComments.length - pinnedCommunityComments.length)"
          >
            {{ $t('loadMore') }}
          </div>
        </div>
        <div
          v-else
          class="community_content column is-three-quarter on-body-background"
        >
          <div v-if="guestbookAccess.reason === 'disabled'">
            <h3 class="title is-3 has-text-centered has-text-weight-bold">
              {{ $t('accessDisabledTitle') }}
            </h3>
            <p class="has-text-centered mb-10">
              {{ $t('accessDisabledDescription') }}
            </p>
          </div>
          <div v-else-if="guestbookAccess.reason === 'authenticated'">
            <h3 class="title is-3 has-text-centered has-text-weight-bold">
              {{ $t('accessAuthenticatedTitle') }}
            </h3>
            <p class="has-text-centered mb-10">
              {{ $t('accessAuthenticatedDescription') }}
            </p>
            <div
              class="button is-fullwidth pt-30 pb-30 mb-10"
              @click="$root.$emit('showLogin', { scrollTo: '#profile-navigation' })"
              v-html="`${$t('loginAndJoinCommunity', { nickname: artist.nickname })}`"
            />
          </div>
          <div v-else-if="guestbookAccess.reason === 'follower'">
            <h3 class="title is-3 has-text-centered has-text-weight-bold">
              {{ $t('accessFollowerTitle') }}
            </h3>
            <p class="has-text-centered mb-10">
              {{ $t('accessFollowerDescription', {
                nickname:
                  artist.nickname
              })
              }}
            </p>
          </div>
          <div v-else-if="guestbookAccess.reason === 'reward'">
            <h3 class="community_content title is-3 has-text-centered has-text-weight-bold">
              {{ $t('accessRewardTitle') }}
            </h3>
            <p
              class="has-text-centered mb-10"
              v-html="$t('accessRewardDescription', { rewardsOverviewUrl: `/${artist.urlAlias}/rewards` })"
            />
          </div>
        </div>
      </div>
    </div>
    <portal
      v-if="activePortal"
      :to="`post_editor_${commentEditorTarget}`"
    >
      <div class="on-tiles-background">
        <editor
          v-if="$store.state.isAuthenticated"
          :editorId="portableEditorId"
          :options="activeEditorOptions"
          :targetPost="null"
          @editormessage="handleEditorMessage"
        />
      </div>
    </portal>
  </section>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import profilePostCommunity from '~/components/profile-post-community'
import sidenav from '~/components/sidenav'
import editor from '~/components/editor'
import artistPageHead from '~/mixins/artistPageHead'

export default {
  mixins: [artistPageHead],

  components: {
    sidenav,
    profilePostCommunity,
    editor
  },

  provide() {
    return {
      moveEditor: this.moveEditor,
      deleteComment: this.deleteComment
    }
  },

  computed: {
    activePortal() {
      return this.commentEditorTarget
    },
    activeEditorOptions() {
      const placeholder = this.targetPost ? `${this.$t('tell')} ${this.targetPost.nickname} ${this.$t('whatThink')}` : this.$t('whatWouldShare')
      return {
        placeholder
      }
    },
    pinnedCommunityComments() {
      return this.artist.communityComments.filter(c => c.pinned)
    },
    regularCommunityComments() {
      return this.artist.communityComments.filter(c => !c.pinned)
    },
    guestbookAccess() {
      if (this.artist.nickname === (this.user || {}).nickname) {
        return {
          granted: true,
          reason: null
        }
      }

      if (!this.artist.guestbookEnabled) {
        return {
          granted: false,
          reason: 'disabled'
        }
      }
      if (!this.isAuthenticated) {
        return {
          granted: false,
          reason: 'authenticated'
        }
      }

      if (this.artist.guestbookState === 'follower' && !this.artist.followed) {
        return {
          granted: false,
          reason: 'follower'
        }
      }

      if (this.artist.guestbookState === 'reward' && !this.artist.subscribed) {
        return {
          granted: false,
          reason: 'reward'
        }
      }

      return {
        granted: true,
        reason: null
      }
    },
    ...mapState(['artist', 'user', 'isAuthenticated'])
  },

  data() {
    return {
      editorOptions: {
        placeholder: this.$t('whatWouldShare')
      },
      commentEditorTarget: null,
      targetPost: null,
      rootEditorId: 'rootEditor',
      portableEditorId: 'portableEditor'
    }
  },

  watch: {
    commentEditorTarget(targetId) {
      console.log(`editor target id changed to ${targetId}.`)
    }
  },

  methods: {
    moveEditor: function (targetPost) {
      this.targetPost = targetPost
      this.commentEditorTarget = targetPost._id
    },
    async handleEditorMessage(data) {
      if (data.command === 'cancel') {
        this.commentEditorTarget = null
        this.targetPost = null
      } else if (data.command === 'save') {
        if (data.editorId === this.rootEditorId) {
          if (this.targetPost && this.targetPost._id === this.commentEditorTarget) {
            this.commentEditorTarget = null
            this.targetPost = null
          }
        }
        await this.save(data)
        this.commentEditorTarget = null
        this.targetPost = null
      }
    },
    handlePinned(data) {
      console.log('handlePinned', data)
      this.$store.dispatch(
        'setCommunityCommentPinned',
        { enabled: data.enabled, commentId: data.comment._id }
      )
      this.$root.$emit('flash', {
        type: `toasts.community.pinned.${data.enabled ? 'enabled' : 'disabled'}`,
        state: 'info',
        flashConfig: {},
        payload: {}
      })
    },
    async deleteComment(comment) {
      const result = await this.apiPost(`/api/private/${this.artist.urlAlias}/community/comment/delete`, {
        commentId: comment._id
      })
      if (result.success) {
        this.$store.dispatch('deleteCommunityComment', comment)
      }
    },
    async save(data, error) {
      console.log('save comment')
      if (!this.isAuthenticated) {
        console.log('Saving comment not allowed (not authenticated).')
        return false
      }
      data = {
        post: {
          parentId: this.activePortal,
          content: data.content
        }
      }
      try {
        const res = await this.apiPost(`/api/private/${this.artist.urlAlias}/community/comment/add`, data)
        if (res.post) {
          this.$store.dispatch('appendCommunityComments', {
            parent: this.targetPost || { ancestors: [] },
            comments: [res.post]
          })
          this.$nextTick(() => {
            if (!res.post.parent) {
              this.$scrollTo(`#comment_${res.post._id}`)
            }
          })
        }
      } catch (e) {
        console.log('Comment could not be send (404)', e)
      }
    },
    ...mapActions(['loadMoreCommunityComments'])
  }
}
</script>

<style lang="scss">
@import "assets/sass/_variables";
.community_content {
  padding-bottom: 100px;

  // Desktop only
  @media screen and (min-width: #{$desktop}) {
    // Ensures consistent height for scroll position maintenance (58px = top menu bar)
    min-height: calc(100vh - 58px);
  }
}
.pinned-comments {
  h3 {
    font-size: 0.85rem;
    text-align: center;
  }
}
</style>
